import React, { useState, useRef, useEffect } from 'react';
import type { ChatbotSourceDocument, Message } from '../types/api.types';
// @ts-ignore
import flagBar from '../assets/flag-bar.svg';
// @ts-ignore
import shieldAlt from '../assets/shield-fill-check.svg';
// @ts-ignore
import calendarIcon from '../assets/calendar2-week.svg';
// @ts-ignore
import fileIcon from '../assets/file-earmark-text.svg';
// @ts-ignore
import cardIcon from '../assets/card-checklist.svg';
// @ts-ignore
import gavelIcon from '../assets/gavel.svg';
// @ts-ignore
import starsIcon from '../assets/stars.svg';
// @ts-ignore
import sendIcon from '../assets/send.svg';
import { getApiBaseUrl } from '../services/api.service';

const ICON_FILTER = 'brightness(0) saturate(100%) invert(21%) sepia(88%) saturate(1305%) hue-rotate(200deg) brightness(97%) contrast(105%)';

interface ChatbotPageProps {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  onSendMessage: (query: string) => Promise<void>;
}

const SourceCard: React.FC<{ source: ChatbotSourceDocument }> = ({ source }) => {
  const score = source.similarity_score !== null ? Math.round(source.similarity_score * 100) : null;
  const fullUrl = source.url ? `${getApiBaseUrl()}${source.url}` : null;

  const handleDownload = async () => {
    if (!fullUrl) return;

    try {
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error('Error al descargar el archivo');

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${source.radicado || source.nombre || 'documento'}.pdf`;

      window.document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      alert('No se pudo descargar el archivo. Por favor, intente nuevamente.');
    }
  };

  return (
    <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs">
      <div className="w-6 h-6 bg-[#E8EFFE] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg className="w-3.5 h-3.5 text-[#0943B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-700 truncate">{source.nombre ?? 'Documento sin nombre'}</p>
        {source.radicado && (
          <p className="text-gray-400 mt-0.5">Radicado: {source.radicado}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {score !== null && (
          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
            {score}%
          </span>
        )}
        {fullUrl && (
          <button
            onClick={handleDownload}
            className="text-[#0943B5]/80 hover:text-[#072d8a] transition-colors"
            title="Descargar documento"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

const TypingIndicator: React.FC = () => (
  <div className="flex items-end gap-3 mb-6">
    <div className="w-8 h-8 bg-[#0943B5] rounded-full flex items-center justify-center flex-shrink-0">
      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v3" />
      </svg>
    </div>
    <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
      <div className="flex gap-1.5 items-center h-5">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  </div>
);

export const ChatbotPage: React.FC<ChatbotPageProps> = ({
  messages,
  isLoading,
  error,
  onSendMessage,
}) => {
  const [input, setInput] = useState('');
  const [showSources, setShowSources] = useState<Record<string, boolean>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    await onSendMessage(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
  };

  const toggleSources = (messageId: string) => {
    setShowSources(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  return (
    <div className="flex-1 bg-white flex flex-col overflow-hidden">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Welcome state */}
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-[#E8EFFE] rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-[#0943B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v3" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Asistente Técnico PQRSD</h2>
              <img src={flagBar} alt="" aria-hidden="true" className="mb-3" />
              <p className="text-gray-500 max-w-md text-sm leading-relaxed mb-8">
                Especializado en normativa de Peticiones, Quejas, Reclamos y Solicitudes según la Ley 1755 de 2015.
                Las respuestas se fundamentan exclusivamente en los documentos de la entidad.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-4xl">
                {([
                  { text: '¿Cuál es el plazo para responder una petición según la Ley 1755?',    icon: calendarIcon },
                  { text: '¿Cómo se clasifica una queja de acuerdo a la normativa vigente?',     icon: gavelIcon },
                  { text: '¿Qué es una PQRSD y cuáles son sus tipos?',                           icon: fileIcon },
                  { text: '¿Cuáles son los requisitos para radicar una petición?',               icon: cardIcon },
                ] as { text: string; icon: string | null }[]).map(({ text, icon }) => (
                  <button
                    key={text}
                    onClick={() => { setInput(text); textareaRef.current?.focus(); }}
                    className="flex flex-row items-start gap-3 text-left text-sm text-gray-600 border border-gray-200 hover:border-[#0943B5]/40 hover:text-[#072d8a] rounded-xl px-4 py-3 transition-all duration-200 hover:bg-[#E8EFFE]"
                  >
                    {icon && (
                      <div className="w-16 h-16 bg-[#E8EFFE] rounded-lg flex items-center justify-center shrink-0">
                        <img src={icon} alt="" aria-hidden="true" className="w-8 h-8" style={{ filter: ICON_FILTER }} />
                      </div>
                    )}
                    <span>{text}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-400">
                <img src={shieldAlt} alt="" aria-hidden="true" className="w-3.5 h-3.5 shrink-0 opacity-50" />
                <span>Las respuestas se basan exclusivamente en documentos de la Agencia de Renovación del Territorio</span>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-end gap-3 mb-6 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              {message.role === 'assistant' ? (
                <div className="w-8 h-8 bg-[#0943B5] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v3" />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 bg-[#0943B5] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}

              <div className={`flex flex-col gap-2 max-w-[75%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    message.role === 'user'
                      ? 'bg-[#0943B5] text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {message.content}
                </div>

                {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                  <div className="w-full">
                    <button
                      onClick={() => toggleSources(message.id)}
                      className="flex items-center gap-1.5 text-xs text-[#0943B5]/80 hover:text-[#072d8a] transition-colors mb-2"
                    >
                      <svg
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${showSources[message.id] ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {message.sources.length} fuente{message.sources.length !== 1 ? 's' : ''} consultada{message.sources.length !== 1 ? 's' : ''}
                    </button>
                    {showSources[message.id] && (
                      <div className="flex flex-col gap-1.5">
                        {message.sources.map((source, idx) => (
                          <SourceCard key={idx} source={source} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {message.role === 'assistant' && message.model && (
                  <p className="text-xs text-gray-300 px-1">
                    {message.model} · {message.tokens_used} tokens
                  </p>
                )}
              </div>
            </div>
          ))}

          {isLoading && <TypingIndicator />}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-700 flex items-start gap-2">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="bg-white px-6 py-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end gap-3 bg-white border border-gray-200 focus-within:border-[#0943B5] focus-within:ring-2 focus-within:ring-[#0943B5]/10 rounded-2xl px-4 py-3 transition-all duration-200">
            <img src={starsIcon} alt="" aria-hidden="true" className="w-5 h-5 shrink-0 mb-0.5" style={{ filter: ICON_FILTER }} />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu consulta... (Enter para enviar, Shift+Enter para nueva línea)"
              rows={1}
              disabled={isLoading}
              className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none outline-none leading-relaxed disabled:opacity-50"
              style={{ minHeight: '24px', maxHeight: '160px' }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="w-9 h-9 bg-[#0943B5] hover:bg-[#072d8a] disabled:bg-gray-200 text-white rounded-full flex items-center justify-center transition-colors duration-200 shrink-0"
              title="Enviar (Enter)"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <img src={sendIcon} alt="" aria-hidden="true" className="w-4 h-4 block" style={{ filter: 'brightness(0) invert(1)' }} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
